import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    green?: Palette['primary'];
    orange?: Palette['primary'];
    greyText?: Palette['primary'];
  }

  interface PaletteOptions {
    green?: Palette['primary'];
    orange?: Palette['primary'];
    greyText?: Palette['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    greyText: true;
  }
}

let baseTheme = createTheme({
  breakpoints: {
    keys: ['xs', 'sm', 'md', 'lg', 'xl'],
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
    unit: 'px',
  },
  palette: {
    primary: {
      main: '#A483FF',
      contrastText: '#FFFFED',
      light: '#C8B4FF',
      dark: '#58468A',
    },
    secondary: {
      main: '#7ACCCA',
      contrastText: '#FFFFFF',
      light: '#96FAF8',
      dark: '#345756',
    },
    error: {
      main: '#FF4B20',
      contrastText: '#FFFFFF',
      light: '#DB431D',
      dark: '#611D0D',
    },
    info: {
      main: '#759AFF',
      contrastText: '#fff',
      light: '#8799CC',
      dark: '#1C2233',
    },
    success: {
      main: 'rgb(51, 208, 103)',
      light: '#99E8B3',
      dark: '#1E9A43',
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    background: {
      default: '#FFFFFF',
    },
    text: {
      primary: '#1E1E1E',
      secondary: '#9E9E9E',
      disabled: '#C7C7C7',
    },
    grey: {
      50: '#fafafa',
      100: '#F6F9FC',
      200: '#F3F5F9',
      300: '#E3E9EF',
      400: '#DAE1E7',
      500: '#AEB4BE',
      600: '#7D879C',
      700: '#4B566B',
      800: '#373F50',
      900: '#2B3445',
      A100: '#f5f5f5',
      A200: '#eeeeee',
      A400: '#bdbdbd',
      A700: '#616161',
    },
    action: {
      selected: '#F1F1F5',
    },
  },
  typography: {
    allVariants: {
      color: '#333333',
      fontFeatureSettings: '"pnum" on, "lnum" on',
    },
    fontFamily:
      'Raleway, Roboto,-apple-system,BlinkMacSystemFont,Segoe UI,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,sans-serif',
    fontSize: 16,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      '@media (max-width: 600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '2rem',
      fontFamily: 'Gotham, sans-serif',
      '@media (max-width: 600px)': {
        fontSize: '1.8rem',
      },
    },
    h3: {
      fontSize: '1.8rem',
      fontWeight: 500,
      '@media (max-width: 600px)': {
        fontSize: '1.65rem',
      },
    },
    h4: {
      fontSize: '1.6rem',
      fontWeight: 500,
      '@media (max-width: 600px)': {
        fontSize: '1.45rem',
      },
    },
    h5: {
      fontSize: '1.4rem',
      fontWeight: 500,
      '@media (max-width: 600px)': {
        fontSize: '1.2rem',
      },
    },
    subtitle1: {
      fontSize: '1.2rem',
      fontWeight: 500,
      '@media (max-width: 600px)': {
        fontSize: '1rem',
      },
    },
    subtitle2: {
      fontSize: '1.1rem',
      fontWeight: 500,
      lineHeight: '1.3rem',
      '@media (max-width: 600px)': {
        fontSize: '0.9rem',
      },
    },
    body1: {
      fontSize: '1rem',
      '@media (max-width: 600px)': {
        fontSize: '0.9rem',
      },
    },
    body2: {
      fontSize: '0.9rem',
      '@media (max-width: 600px)': {
        fontSize: '0.8rem',
      },
    },
    caption: {
      fontSize: '0.8rem',
      '@media (max-width: 600px)': {
        fontSize: '0.7rem',
      },
    },
  },
  shape: {
    borderRadius: 5,
  },
  components: {
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          '&:focus:not(:focus-visible)': { outline: 'none' },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus:not(:focus-visible)': { outline: 'none' },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 18,
          [`&.Mui-disabled`]: {
            backgroundColor: 'rgba(221, 221, 221, 0.85)',
            color: 'rgba(0, 0, 0, 0.35)',
            cursor: 'not-allowed',
          },
          '&:focus:not(:focus-visible)': { outline: 'none' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 18,
          [`&.Mui-disabled`]: {
            backgroundColor: 'rgba(221, 221, 221, 0.85)',
            color: 'rgba(0, 0, 0, 0.35)',
            cursor: 'not-allowed',
          },
          [`&.Mui-disabled.MuiButton-text`]: {
            backgroundColor: 'transparent',
          },
        },
        sizeLarge: {
          fontWeight: 700,
          fontSize: '0.95rem',
          paddingInline: 50,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          padding: '24px 42px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '0 0 16px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 0 0',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '0',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiInputBase-adornedStart': {
            columnGap: '10px',
          },
          '& .MuiInputBase-adornedEnd': {
            columnGap: '10px',
          },
        },
      },
      variants: [
        {
          props: { size: 'small', multiline: false },
          style: {
            '& .MuiOutlinedInput-root': {
              height: '40px',
              fontSize: '12px',
              fontWeight: 550,
            },
          },
        },
        {
          props: { size: 'medium', multiline: false },
          style: {
            '& .MuiOutlinedInput-root': {
              height: '50px',
            },
          },
        },
      ],
    },
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(3, 0, 71, 0.09)',
    '0px 4px 16px rgba(43, 52, 69, 0.1)',
    '0px 8px 45px rgba(3, 0, 71, 0.09)',
    '0px 4px 10px rgba(0, 0, 0, 0.15)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
});

baseTheme = createTheme(baseTheme, {
  palette: {
    green: baseTheme.palette.augmentColor({
      color: {
        main: '#D1D159',
      },
      name: 'green',
    }),
    orange: baseTheme.palette.augmentColor({
      color: {
        main: '#F98B53',
      },
      name: 'orange',
    }),
    greyText: baseTheme.palette.augmentColor({
      color: {
        main: '#7D879C',
      },
      name: 'greyText',
    }),
  },
});

export default baseTheme;
